import { useState } from 'react';
import { ApiError } from '../../api/client';
import type { CreateDraftResponse, FormValues, Recipient, ValidationIssue } from '../../api/types';

interface SendPanelProps {
  values: FormValues;
  hasErrors: boolean;

  /**
   * Set when there is no mailbox to create a draft in, with the reason. Shown up front rather than
   * on click: a button that always fails is worse than one that explains itself.
   */
  draftUnavailableReason: string | null;

  /**
   * Reveals errors for fields the user has not touched yet. Called instead of submitting, so the
   * button stays clickable and pressing it always produces feedback rather than nothing.
   */
  onRevealErrors: () => void;

  createDraft: (request: { to: Recipient[]; acknowledgeWarnings: boolean }) => Promise<CreateDraftResponse>;

  onConsentRequired: (scopes: string[]) => void;
}

export function SendPanel({
  values,
  hasErrors,
  draftUnavailableReason,
  onRevealErrors,
  createDraft,
  onConsentRequired,
}: SendPanelProps) {
  const [toAddress, setToAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateDraftResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingWarnings, setPendingWarnings] = useState<ValidationIssue[] | null>(null);

  // The new hire's first name is already in the form; reuse it as the recipient display name so the
  // draft reads "Jordan <jordan@…>" rather than a bare address.
  const recipientName = values.firstName?.trim() || null;

  async function submit(acknowledgeWarnings: boolean) {
    // Reveal what is still missing rather than doing nothing. The API would reject this anyway, but a
    // round trip to be told what the client already knows is a worse experience.
    if (hasErrors) {
      onRevealErrors();
      setError('Some required fields are still empty. They are now highlighted above.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const draft = await createDraft({
        to: [{ address: toAddress.trim(), name: recipientName }],
        acknowledgeWarnings,
      });

      setResult(draft);
      setPendingWarnings(null);
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.requiresAcknowledgement) {
          setPendingWarnings(caught.issues);
        } else if (caught.consentRequired) {
          onConsentRequired(caught.consentScopes);
        } else {
          const fieldMessages = Object.values(caught.fieldErrors).flat();
          setError(fieldMessages.length > 0 ? fieldMessages.join(' ') : caught.message);
        }
      } else {
        setError(caught instanceof Error ? caught.message : 'Something went wrong creating the draft.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <section className="send send--done" aria-label="Draft created">
        <h2 className="send__title">Draft created in your Outlook</h2>
        <p className="send__body">
          It is in your <strong>Drafts</strong> folder with the subject “{result.subject}”. Open it,
          read it once more, and press Send.
        </p>

        {result.webLink && (
          <a className="button button--primary" href={result.webLink} target="_blank" rel="noreferrer">
            Open the draft in Outlook
          </a>
        )}

        <p className="send__hint">
          Opening it in Outlook on the web is safest. The desktop client can rewrite HTML when it opens
          a draft, so if the layout looks wrong there, send from the web client instead.
        </p>

        <button type="button" className="button button--quiet" onClick={() => setResult(null)}>
          Create another
        </button>
      </section>
    );
  }

  return (
    <section className="send" aria-label="Create the draft">
      <h2 className="send__title">Create the draft</h2>

      <div className="field">
        <label className="field__label" htmlFor="to-address">
          Send to
          <span className="field__required" aria-label="required">
            {' '}
            *
          </span>
        </label>
        <input
          id="to-address"
          className="control"
          type="email"
          value={toAddress}
          disabled={draftUnavailableReason !== null}
          onChange={(e) => setToAddress(e.target.value)}
        />
      </div>

      {draftUnavailableReason !== null ? (
        <p className="send__unavailable">{draftUnavailableReason}</p>
      ) : (
        <>
          {pendingWarnings && (
            <div className="confirm" role="alertdialog" aria-labelledby="confirm-title">
              <h3 className="confirm__title" id="confirm-title">
                Create it anyway?
              </h3>
              <ul>
                {pendingWarnings.map((issue, index) => (
                  <li key={index}>{issue.message}</li>
                ))}
              </ul>
              <div className="confirm__actions">
                <button
                  type="button"
                  className="button button--primary"
                  disabled={submitting}
                  onClick={() => submit(true)}
                >
                  Yes, create the draft
                </button>
                <button type="button" className="button button--quiet" onClick={() => setPendingWarnings(null)}>
                  Let me fix it
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="send__error" role="alert">
              {error}
            </p>
          )}

          {!pendingWarnings && (
            <button
              type="button"
              className="button button--primary button--wide"
              disabled={submitting || toAddress.trim().length === 0}
              onClick={() => submit(false)}
            >
              {submitting ? 'Creating…' : 'Create draft in Outlook'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
